<template>
  <div v-if="isVisible" class="modal-overlay" @click="handleOverlayClick">
    <div class="carousel-modal" @click.stop>
      <!-- Close Button -->
      <button class="close-btn" @click="closeModal">×</button>

              <!-- Header with Title -->
        <div class="modal-header">
          <h3 class="modal-title">{{ title }}</h3>
        </div>

      <!-- Navigation Buttons -->
      <button
        class="nav-btn nav-btn-left"
        :disabled="currentPage === 1"
        @click="previousImage"
      >
        ❮
      </button>

      <button
        class="nav-btn nav-btn-right"
        :disabled="currentPage === totalPages"
        @click="nextImage"
      >
        ❯
      </button>

      <!-- Main Content Area with Zoom/Pan -->
      <div class="image-container">
        <div class="loading" v-if="isLoading">
          <div class="spinner"></div>
          Loading...
        </div>

        <div
          class="zoom-container"
          ref="zoomContainer"
          :class="{
            dragging: zoomState.isDragging,
            zoomed: zoomState.scale > 1,
          }"
          v-show="!isLoading"
        >
          <div class="interaction-hint" ref="interactionHint">
            <span ref="hintText">Scroll to zoom • Drag to pan</span>
          </div>

          <div class="zoom-content" ref="zoomContent">
            <img
              ref="imageElement"
              :src="currentImageSrc"
              :alt="`Page ${currentPage} of ${totalPages}`"
              class="carousel-image"
              @load="onImageLoad"
              @error="onImageError"
            />
          </div>
        </div>

        <div v-if="imageError" class="error-message">
          Failed to load image: {{ currentImageSrc }}
        </div>
      </div>

              <!-- Page Counter -->
        <div class="page-counter-bottom">
          {{ currentPage }}/{{ totalPages }}
        </div>
        
        <!-- Footer with Keyboard Hints -->
        <div class="modal-footer">
          <div class="keyboard-hints">
            Use ← → arrow keys to navigate • Double-click to reset zoom
          </div>
        </div>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, watch, onMounted, onUnmounted, nextTick } from "vue";

// Props
const props = defineProps({
  isVisible: {
    type: Boolean,
    default: false,
  },
  title: {
    type: String,
    default: "Image Gallery",
  },
  imagePath: {
    type: String,
    required: true,
  },
  totalImages: {
    type: Number,
    required: true,
  },
  imageFilePattern: {
    type: String,
    default: "page_{number}.webp", // Default pattern, {number} will be replaced
  },
  startPage: {
    type: Number,
    default: 1,
  },
});

// Emits
const emit = defineEmits(["close", "pageChange"]);

// Reactive state
const currentPage = ref(props.startPage);
const isLoading = ref(false);
const imageError = ref(false);

// Refs for DOM elements
const zoomContainer = ref(null);
const zoomContent = ref(null);
const imageElement = ref(null);
const interactionHint = ref(null);
const hintText = ref(null);

// Computed properties
const totalPages = computed(() => props.totalImages);

const currentImageSrc = computed(() => {
  if (!props.imagePath || !props.imageFilePattern) return "";

  const pageNumber = currentPage.value.toString();
  const fileName = props.imageFilePattern.replace("{number}", pageNumber);

  // For public directory paths, simply concatenate
  const fullPath = `${props.imagePath}/${fileName}`;
  console.log("Loading image:", fullPath);
  return fullPath;
});

// Zoom and Pan State (adapted from odour_impact.html)
const zoomState = ref({
  scale: 1,
  translateX: 0,
  translateY: 0,
  isDragging: false,
  lastMouseX: 0,
  lastMouseY: 0,
  minScale: 0.5,
  maxScale: 5,
  // Touch handling
  touches: [],
  lastTouchDistance: 0,
  isTouch: false,
});

// Interaction Hint Functions
let hintTimeout;

const showInteractionHint = () => {
  if (!interactionHint.value) return;

  // Update hint text based on device type
  const isMobile = "ontouchstart" in window;
  if (hintText.value) {
    hintText.value.textContent = isMobile
      ? "Pinch to zoom • Drag to pan"
      : "Scroll to zoom • Drag to pan";
  }

  interactionHint.value.classList.add("show");

  // Auto-hide after 4 seconds
  clearTimeout(hintTimeout);
  hintTimeout = setTimeout(() => {
    hideInteractionHint();
  }, 4000);
};

const hideInteractionHint = () => {
  if (!interactionHint.value) return;
  interactionHint.value.classList.remove("show");
  interactionHint.value.classList.add("hide");

  setTimeout(() => {
    interactionHint.value?.classList.remove("hide");
  }, 300);
};

const hideInteractionHintImmediately = () => {
  if (!interactionHint.value) return;
  clearTimeout(hintTimeout);
  interactionHint.value.classList.remove("show");
};

// Zoom and Pan Functions (adapted from odour_impact.html)
const applyTransform = () => {
  if (zoomContent.value) {
    zoomContent.value.style.transform = `scale(${zoomState.value.scale}) translate(${zoomState.value.translateX}px, ${zoomState.value.translateY}px)`;
  }
};

const resetZoom = () => {
  zoomState.value.scale = 1;
  zoomState.value.translateX = 0;
  zoomState.value.translateY = 0;
  applyTransform();
};

const constrainPan = () => {
  if (!zoomContainer.value || zoomState.value.scale <= 1) {
    zoomState.value.translateX = 0;
    zoomState.value.translateY = 0;
    return;
  }

  const containerRect = zoomContainer.value.getBoundingClientRect();
  const scaledWidth = containerRect.width * zoomState.value.scale;
  const scaledHeight = containerRect.height * zoomState.value.scale;

  const maxTranslateX =
    (scaledWidth - containerRect.width) / (2 * zoomState.value.scale);
  const maxTranslateY =
    (scaledHeight - containerRect.height) / (2 * zoomState.value.scale);

  zoomState.value.translateX = Math.max(
    -maxTranslateX,
    Math.min(maxTranslateX, zoomState.value.translateX)
  );
  zoomState.value.translateY = Math.max(
    -maxTranslateY,
    Math.min(maxTranslateY, zoomState.value.translateY)
  );
};

const zoomToPoint = (clientX, clientY, zoomFactor) => {
  if (!zoomContainer.value) return;

  const rect = zoomContainer.value.getBoundingClientRect();
  const offsetX = clientX - rect.left - rect.width / 2;
  const offsetY = clientY - rect.top - rect.height / 2;

  const newScale = Math.max(
    zoomState.value.minScale,
    Math.min(zoomState.value.maxScale, zoomState.value.scale * zoomFactor)
  );

  if (newScale !== zoomState.value.scale) {
    const scaleRatio = newScale / zoomState.value.scale;
    zoomState.value.translateX =
      (zoomState.value.translateX - offsetX / zoomState.value.scale) *
        scaleRatio +
      offsetX / newScale;
    zoomState.value.translateY =
      (zoomState.value.translateY - offsetY / zoomState.value.scale) *
        scaleRatio +
      offsetY / newScale;
    zoomState.value.scale = newScale;

    constrainPan();
    applyTransform();
  }
};

// Event Handlers
const handleMouseWheel = (e) => {
  e.preventDefault();
  hideInteractionHintImmediately();
  const zoomFactor = e.deltaY > 0 ? 0.9 : 1.1;
  zoomToPoint(e.clientX, e.clientY, zoomFactor);
};

const handleMouseDown = (e) => {
  if (e.button !== 0) return; // Only left mouse button

  hideInteractionHintImmediately();
  zoomState.value.isDragging = true;
  zoomState.value.lastMouseX = e.clientX;
  zoomState.value.lastMouseY = e.clientY;
  if (zoomContent.value) {
    zoomContent.value.classList.add("no-transition");
  }

  e.preventDefault();
};

const handleMouseMove = (e) => {
  if (!zoomState.value.isDragging || zoomState.value.scale <= 1) return;

  const deltaX = e.clientX - zoomState.value.lastMouseX;
  const deltaY = e.clientY - zoomState.value.lastMouseY;

  zoomState.value.translateX += deltaX / zoomState.value.scale;
  zoomState.value.translateY += deltaY / zoomState.value.scale;

  constrainPan();
  applyTransform();

  zoomState.value.lastMouseX = e.clientX;
  zoomState.value.lastMouseY = e.clientY;
};

const handleMouseUp = () => {
  zoomState.value.isDragging = false;
  if (zoomContent.value) {
    zoomContent.value.classList.remove("no-transition");
  }
};

const handleDoubleClick = (e) => {
  e.preventDefault();
  if (zoomState.value.scale > 1) {
    resetZoom();
  } else {
    zoomToPoint(e.clientX, e.clientY, 2);
  }
};

// Touch Event Handlers (simplified version)
const getTouchDistance = (touch1, touch2) => {
  const dx = touch1.clientX - touch2.clientX;
  const dy = touch1.clientY - touch2.clientY;
  return Math.sqrt(dx * dx + dy * dy);
};

const getTouchCenter = (touch1, touch2) => {
  return {
    x: (touch1.clientX + touch2.clientX) / 2,
    y: (touch1.clientY + touch2.clientY) / 2,
  };
};

const handleTouchStart = (e) => {
  hideInteractionHintImmediately();
  zoomState.value.isTouch = true;
  zoomState.value.touches = Array.from(e.touches);

  if (e.touches.length === 1) {
    // Single touch - start panning
    zoomState.value.isDragging = true;
    zoomState.value.lastMouseX = e.touches[0].clientX;
    zoomState.value.lastMouseY = e.touches[0].clientY;
    if (zoomContent.value) {
      zoomContent.value.classList.add("no-transition");
    }
  } else if (e.touches.length === 2) {
    // Two fingers - start pinch zoom
    zoomState.value.isDragging = false;
    zoomState.value.lastTouchDistance = getTouchDistance(
      e.touches[0],
      e.touches[1]
    );
    if (zoomContent.value) {
      zoomContent.value.classList.add("no-transition");
    }
  }

  e.preventDefault();
};

const handleTouchMove = (e) => {
  if (!zoomState.value.isTouch) return;

  if (
    e.touches.length === 1 &&
    zoomState.value.isDragging &&
    zoomState.value.scale > 1
  ) {
    // Single finger panning
    const deltaX = e.touches[0].clientX - zoomState.value.lastMouseX;
    const deltaY = e.touches[0].clientY - zoomState.value.lastMouseY;

    zoomState.value.translateX += deltaX / zoomState.value.scale;
    zoomState.value.translateY += deltaY / zoomState.value.scale;

    constrainPan();
    applyTransform();

    zoomState.value.lastMouseX = e.touches[0].clientX;
    zoomState.value.lastMouseY = e.touches[0].clientY;
  } else if (e.touches.length === 2) {
    // Two finger pinch zoom
    const currentDistance = getTouchDistance(e.touches[0], e.touches[1]);
    const center = getTouchCenter(e.touches[0], e.touches[1]);

    if (zoomState.value.lastTouchDistance > 0) {
      const zoomFactor = currentDistance / zoomState.value.lastTouchDistance;
      zoomToPoint(center.x, center.y, zoomFactor);
    }

    zoomState.value.lastTouchDistance = currentDistance;
  }

  e.preventDefault();
};

const handleTouchEnd = (e) => {
  if (e.touches.length === 0) {
    // All fingers lifted
    zoomState.value.isDragging = false;
    zoomState.value.isTouch = false;
    zoomState.value.lastTouchDistance = 0;
    if (zoomContent.value) {
      zoomContent.value.classList.remove("no-transition");
    }
  } else if (e.touches.length === 1) {
    // One finger remaining - switch to pan mode
    zoomState.value.isDragging = true;
    zoomState.value.lastMouseX = e.touches[0].clientX;
    zoomState.value.lastMouseY = e.touches[0].clientY;
    zoomState.value.lastTouchDistance = 0;
  }

  zoomState.value.touches = Array.from(e.touches);
  e.preventDefault();
};

// Initialize zoom/pan event listeners
const initZoomPan = () => {
  if (!zoomContainer.value) return;

  // Mouse events
  zoomContainer.value.addEventListener("wheel", handleMouseWheel, {
    passive: false,
  });
  zoomContainer.value.addEventListener("mousedown", handleMouseDown);
  zoomContainer.value.addEventListener("dblclick", handleDoubleClick);

  // Touch events
  zoomContainer.value.addEventListener("touchstart", handleTouchStart, {
    passive: false,
  });
  zoomContainer.value.addEventListener("touchmove", handleTouchMove, {
    passive: false,
  });
  zoomContainer.value.addEventListener("touchend", handleTouchEnd, {
    passive: false,
  });

  // Prevent context menu on right click
  zoomContainer.value.addEventListener("contextmenu", (e) =>
    e.preventDefault()
  );
};

// Navigation Functions
const nextImage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
    resetZoom();
    emit("pageChange", currentPage.value);
  }
};

const previousImage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
    resetZoom();
    emit("pageChange", currentPage.value);
  }
};

// Modal Functions
const closeModal = () => {
  resetZoom();
  hideInteractionHintImmediately();
  emit("close");
};

const handleOverlayClick = () => {
  closeModal();
};

// Image Loading Functions
const onImageLoad = () => {
  isLoading.value = false;
  imageError.value = false;

  // Show interaction hint after a brief delay
  setTimeout(() => {
    showInteractionHint();
  }, 200);
};

const onImageError = () => {
  isLoading.value = false;
  imageError.value = true;
};

// Keyboard Navigation
const handleKeydown = (e) => {
  if (!props.isVisible) return;

  switch (e.key) {
    case "ArrowLeft":
      e.preventDefault();
      previousImage();
      break;
    case "ArrowRight":
      e.preventDefault();
      nextImage();
      break;
    case "Escape":
      e.preventDefault();
      closeModal();
      break;
  }
};

// Watchers
watch(
  () => props.isVisible,
  async (newValue) => {
    if (newValue) {
      currentPage.value = props.startPage;
      resetZoom();
      isLoading.value = true;
      imageError.value = false;

      await nextTick();
      initZoomPan();
    }
  }
);

watch(currentPage, () => {
  isLoading.value = true;
  imageError.value = false;
  resetZoom();
});

// Lifecycle
onMounted(() => {
  document.addEventListener("mousemove", handleMouseMove);
  document.addEventListener("mouseup", handleMouseUp);
  document.addEventListener("keydown", handleKeydown);
});

onUnmounted(() => {
  document.removeEventListener("mousemove", handleMouseMove);
  document.removeEventListener("mouseup", handleMouseUp);
  document.removeEventListener("keydown", handleKeydown);
  clearTimeout(hintTimeout);
});
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 10000;
  backdrop-filter: blur(5px);
}

.carousel-modal {
  position: relative;
  width: 95vw;
  height: 90vh;
  max-width: 1200px;
  background: linear-gradient(145deg, #ffffff 0%, #f8fffe 100%);
  border-radius: 0px;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  box-shadow: 0 25px 50px rgba(0, 0, 0, 0.5);
}

.close-btn {
  position: absolute;
  top: 15px;
  right: 15px;
  background: rgba(0, 135, 104, 0.9);
  color: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  font-size: 24px;
  font-weight: bold;
  cursor: pointer;
  z-index: 1001;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;
  backdrop-filter: blur(10px);
}

.close-btn:hover {
  background: rgba(0, 135, 104, 1);
  transform: scale(1.1);
}

  .modal-header {
    background: linear-gradient(135deg, #008768 0%, #00a085 100%);
    color: white;
    padding: 20px 25px;
    display: flex;
    justify-content: center;
    align-items: center;
    position: relative;
  }

.modal-header::before {
  content: "";
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: linear-gradient(
    135deg,
    rgba(255, 255, 255, 0.1) 0%,
    rgba(255, 255, 255, 0.05) 100%
  );
  pointer-events: none;
}

.modal-title {
  margin: 0;
  font-size: 20px;
  font-weight: 600;
  letter-spacing: 0.3px;
  position: relative;
  z-index: 1;
}

  .page-counter-bottom {
    position: absolute;
    bottom: 15px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 135, 104, 0.95);
    color: white;
    padding: 12px 20px;
    border-radius: 25px;
    font-size: 16px;
    font-weight: 600;
    z-index: 1001;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
    pointer-events: none;
  }

.nav-btn {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  background: rgba(0, 135, 104, 0.9);
  color: white;
  border: none;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  font-size: 20px;
  font-weight: bold;
  cursor: pointer;
  z-index: 1000;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(10px);
  border: 1px solid rgba(255, 255, 255, 0.2);
}

.nav-btn:hover:not(:disabled) {
  background: rgba(0, 135, 104, 1);
  transform: translateY(-50%) scale(1.1);
}

.nav-btn:disabled {
  background: rgba(128, 128, 128, 0.5);
  cursor: not-allowed;
  opacity: 0.4;
}

.nav-btn-left {
  left: 20px;
}

.nav-btn-right {
  right: 20px;
}

.image-container {
  flex: 1;
  position: relative;
  overflow: hidden;
  background: linear-gradient(145deg, #fafffe 0%, #f0f9f7 100%);
}

.loading {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  display: flex;
  align-items: center;
  gap: 15px;
  color: #008768;
  font-size: 18px;
  font-weight: 600;
  background: rgba(255, 255, 255, 0.98);
  padding: 30px 40px;
  border-radius: 20px;
  border: 1px solid rgba(255, 255, 255, 0.8);
  z-index: 100;
}

.spinner {
  width: 24px;
  height: 24px;
  border: 3px solid rgba(0, 135, 104, 0.2);
  border-top: 3px solid #008768;
  border-radius: 50%;
  animation: spin 1.2s cubic-bezier(0.4, 0, 0.2, 1) infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.zoom-container {
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
  cursor: grab;
  touch-action: none;
}

.zoom-container.dragging {
  cursor: grabbing;
}

.zoom-container.zoomed {
  cursor: move;
}

  .interaction-hint {
    position: absolute;
    top: 15px;
    left: 50%;
    transform: translateX(-50%);
    background: rgba(0, 135, 104, 0.95);
    color: white;
    padding: 12px 20px;
    border-radius: 25px;
    font-size: 14px;
    font-weight: 500;
    z-index: 1000;
    opacity: 0;
    transition: all 0.3s ease;
    pointer-events: none;
    backdrop-filter: blur(10px);
    border: 1px solid rgba(255, 255, 255, 0.2);
  }

.interaction-hint.show {
  opacity: 1;
  transform: translateX(-50%) translateY(0);
}

.interaction-hint.hide {
  opacity: 0;
  transform: translateX(-50%) translateY(-10px);
}

.zoom-content {
  width: 100%;
  height: 100%;
  transform-origin: center center;
  transition: transform 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;
}

.zoom-content.no-transition {
  transition: none;
}

.carousel-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
  user-select: none;
  -webkit-user-select: none;
  -moz-user-select: none;
  pointer-events: none;
  border-radius: 8px;
}

.error-message {
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
  color: #dc3545;
  font-size: 16px;
  text-align: center;
  background: rgba(255, 255, 255, 0.9);
  padding: 20px;
  border-radius: 10px;
  border: 1px solid #dc3545;
}

.modal-footer {
  display: none;
  background: linear-gradient(145deg, #f8f9fa 0%, #e9ecef 100%);
  padding: 15px 25px;
  border-top: 1px solid rgba(0, 135, 104, 0.1);
}

.keyboard-hints {
  text-align: center;
  color: #6c757d;
  font-size: 13px;
  font-weight: 500;
}

/* Mobile Responsive */
@media screen and (max-width: 768px) {
  .carousel-modal {
    width: 98vw;
    height: 95vh;
    border-radius: 15px;
  }

      .modal-header {
      padding: 15px 20px;
    }

    .modal-title {
      font-size: 18px;
    }

    .page-counter-bottom {
      padding: 10px 16px;
      font-size: 14px;
      bottom: 12px;
    }

  .nav-btn {
    width: 45px;
    height: 45px;
    font-size: 18px;
  }

  .nav-btn-left {
    left: 15px;
  }

  .nav-btn-right {
    right: 15px;
  }

  .close-btn {
    width: 35px;
    height: 35px;
    font-size: 20px;
    top: 12px;
    right: 15px;
  }

      .interaction-hint {
      font-size: 13px;
      padding: 10px 16px;
      top: 12px;
    }

  .loading {
    font-size: 16px;
    padding: 25px 30px;
  }

  .modal-footer {
    padding: 12px 20px;
  }

  .keyboard-hints {
    font-size: 12px;
  }
}

@media screen and (max-width: 480px) {
  .modal-header {
    flex-direction: column;
    gap: 10px;
    align-items: flex-start;
    padding: 12px 16px;
  }

      .modal-title {
      font-size: 16px;
    }

    .page-counter-bottom {
      padding: 8px 14px;
      font-size: 13px;
      bottom: 10px;
    }

  .nav-btn {
    width: 40px;
    height: 40px;
    font-size: 16px;
  }

  .nav-btn-left {
    left: 10px;
  }

  .nav-btn-right {
    right: 10px;
  }

  .close-btn {
    width: 32px;
    height: 32px;
    font-size: 18px;
    top: 10px;
    right: 12px;
  }
}
</style>
